import { onlineManager } from '@tanstack/react-query';
import {
  EFFECTIVE_CONNECTIVITY,
  getEffectiveConnectivity,
  shouldPauseQueriesForConnectivity,
  subscribeEffectiveConnectivity,
} from '../network/effectiveConnectivity';

import { runOfflineSendRefetchHandlers } from './offlineSendReconcile';

let lastConnectivity = getEffectiveConnectivity();
let resumeChain = Promise.resolve();

function canResumeMutationsNow() {
  return onlineManager.isOnline() && !shouldPauseQueriesForConnectivity();
}

function afterResumeComplete() {
  runOfflineSendRefetchHandlers();
}

/**
 * Replay paused offline mutations when connectivity / onlineManager allow sends.
 * Serializes resume calls to preserve ordering guarantees from mutation scopes.
 */
export function initOfflineMutationResume(queryClient) {
  const tryResume = (reason) => {
    if (!canResumeMutationsNow()) return;
    resumeChain = resumeChain
      .then(() => queryClient.resumePausedMutations())
      .then(() => afterResumeComplete())
      .catch((err) => console.warn(`[mutations] resumePausedMutations failed (${reason}):`, err));
  };

  const unsubscribeConnectivity = subscribeEffectiveConnectivity(() => {
    const next = getEffectiveConnectivity();
    const wasUnreachable = lastConnectivity === EFFECTIVE_CONNECTIVITY.UNREACHABLE;
    const nowOnline = next !== EFFECTIVE_CONNECTIVITY.UNREACHABLE;

    if (wasUnreachable && nowOnline) {
      runOfflineSendRefetchHandlers();
      tryResume('connectivity-online');
    }
    lastConnectivity = next;
  });

  const unsubscribeOnlineManager = onlineManager.subscribe((online) => {
    if (online) {
      runOfflineSendRefetchHandlers();
      tryResume('online-manager');
    }
  });

  tryResume('init');

  return () => {
    unsubscribeConnectivity();
    unsubscribeOnlineManager();
  };
}

export function resumeOfflineMutationsNow(queryClient, reason = 'manual') {
  if (!canResumeMutationsNow()) return Promise.resolve();
  return queryClient
    .resumePausedMutations()
    .then(() => afterResumeComplete())
    .catch((err) => {
      console.warn(`[mutations] resumePausedMutations failed (${reason}):`, err);
    });
}
